import type { SxProps, Theme } from "@mui/material/styles";

import { Autocomplete, Box, Skeleton, TextField, Tooltip, Typography } from "@mui/material";
import { useMemo } from "react";
import {
  DocumentReviewStatus,
  getDocumentReviewStatusFromRow,
  getDocumentReviewStatusIcon,
} from "util/documentReviewStatus";

import { useReviewDocumentsList } from "../hooks/useReviewDocumentsList";

type DocumentSwitcherOption = {
  id: string;
  title: string;
  statusIcon?: string;
  displayIndex: number;
};

type DocumentSwitcherSelectProps = {
  label?: string;
  documentIds: string[];
  tenantId?: string;
  selectedId?: string;
  skip?: boolean;
  disabled?: boolean;
  onSelect: (documentId: string) => void;
  minWidth?: number | string;
  maxWidth?: number | string;
};

export function DocumentSwitcherSelect({
  label = "Document",
  documentIds,
  tenantId,
  selectedId,
  skip = false,
  disabled = false,
  onSelect,
  minWidth = 320,
  maxWidth = 360,
}: DocumentSwitcherSelectProps) {
  const { documentsById, loading } = useReviewDocumentsList({
    documentIds,
    tenantId,
    skip,
  });

  const options = useMemo<DocumentSwitcherOption[]>(
    () =>
      documentIds.map((id, index) => {
        const doc = documentsById[id];
        if (!doc) {
          return {
            id,
            title: id,
            statusIcon: "",
            displayIndex: index + 1,
          };
        }

        const status = getDocumentReviewStatusFromRow(doc);
        const statusIcon =
          status === DocumentReviewStatus.Failed
            ? "⚠️"
            : selectedId === id
              ? "👀"
              : getDocumentReviewStatusIcon(status);

        return {
          id,
          title: doc.title ?? id,
          statusIcon,
          displayIndex: index + 1,
        };
      }),
    [documentIds, documentsById, selectedId]
  );

  const value = useMemo(
    () => options.find((option) => option.id === selectedId) ?? null,
    [options, selectedId]
  );

  const containerSx = useMemo<SxProps<Theme>>(() => {
    const base: SxProps<Theme> = {
      flexShrink: 0,
      minWidth,
    };
    if (maxWidth != null) {
      return {
        ...base,
        maxWidth,
        width: maxWidth,
      };
    }
    return {
      ...base,
      width: minWidth,
    };
  }, [maxWidth, minWidth]);

  return (
    <Box sx={containerSx}>
      {loading ? (
        <Skeleton variant="rounded" height={56} />
      ) : (
        <Autocomplete
          value={value}
          disabled={disabled}
          options={options}
          fullWidth
          sx={{ width: "100%" }}
          onChange={(_event, newValue) => {
            if (newValue) {
              onSelect(newValue.id);
            }
          }}
          getOptionLabel={(option) => option.title}
          isOptionEqualToValue={(option, optionValue) => option.id === optionValue.id}
          renderInput={(params) => <TextField {...params} label={label} />}
          renderOption={(props, option) => (
            <Box
              component="li"
              {...props}
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 2,
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  minWidth: 0,
                }}
              >
                {option.statusIcon ? (
                  <Typography component="span" sx={{ flexShrink: 0 }}>
                    {option.statusIcon}
                  </Typography>
                ) : null}
                <Box sx={{ minWidth: 0 }}>
                  <Tooltip title={option.title} placement="left">
                    <Typography variant="body2" noWrap>
                      {option.title}
                    </Typography>
                  </Tooltip>
                </Box>
              </Box>
              <Typography variant="caption" color="text.secondary">
                {option.displayIndex}
              </Typography>
            </Box>
          )}
        />
      )}
    </Box>
  );
}
